import PropTypes from 'prop-types'
import queryString from 'query-string'

import * as libraryResourceIcons from '../img/library_resource_icons'

export const libraryAssetsUrlSeparator = '---'

export const defaultState = {
  pagination: {
    page: 1,
    size: 12,
  },
  sort: {
    field: 'title',
    order: 'asc',
  },
  filters: {
    type: [],
    name: [],
    query: null,
  },
}

export const resourceTypeUrlMapping = {
  'casi-session': 'casiSession',
  'catalog-training': 'catalogTraining',
  'category-brochure': 'categoryBrochure',
  'journal-article': 'journalArticle',
  'literature-reference': 'literatureReference',
  'patient-brochure': 'patientBrochure',
  'patient-education': 'patientEducation',
  'pharmacy-content': 'pharmacyContent',
  'private-label-form': 'privateLabelForm',
  'product-reference-chart': 'productReferenceChart',
  protocol: 'protocol',
  'social-media-resource': 'socialMediaResource',
  'tech-sheet': 'techSheet',
  'tech-sheet-french': 'techSheetFrench',
  'price-list': 'priceList',
  'white-paper': 'whitePaper',
  webinar: 'webinar',
}

export const professionalResourceTypes = [
  'casiSession',
  'catalogTraining',
  'categoryBrochure',
  'journalArticle',
  'literatureReference',
  'patientBrochure',
  'patientEducation',
  'pharmacyContent',
  'privateLabelForm',
  'productReferenceChart',
  'protocol',
  'socialMediaResource',
  'techSheet',
  'techSheetFrench',
  'priceList',
  'whitePaper',
  'webinar',
]

export const publicResourceTypes = ['casiSession', 'patientBrochure', 'patientEducation']

export const nameFilterUrlMapping = {
  'a-f': 'AtoF',
  'g-m': 'GtoM',
  'n-z': 'NtoZ',
}

export const libraryPropTypes = {
  searchOptions: PropTypes.shape({
    pagination: PropTypes.shape({
      page: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired,
    }).isRequired,
    sort: PropTypes.shape({
      field: PropTypes.string.isRequired,
      order: PropTypes.string.isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      type: PropTypes.arrayOf(PropTypes.string).isRequired,
      name: PropTypes.arrayOf(PropTypes.string).isRequired,
      query: PropTypes.string,
    }).isRequired,
  }),
  meta: PropTypes.shape({
    count: PropTypes.shape({
      all: PropTypes.number.isRequired,
      casiSession: PropTypes.number,
      catalogTraining: PropTypes.number,
      categoryBrochure: PropTypes.number,
      journalArticle: PropTypes.number,
      literatureReference: PropTypes.number,
      patientBrochure: PropTypes.number,
      patientEducation: PropTypes.number,
      pharmacyContent: PropTypes.number,
      productReferenceChart: PropTypes.number,
      protocol: PropTypes.number,
      socialMediaResource: PropTypes.number,
      techSheet: PropTypes.number,
      techSheetFrench: PropTypes.number,
      priceList: PropTypes.number,
      privateLabelForm: PropTypes.number,
      whitePaper: PropTypes.number,
      webinar: PropTypes.number,
    }),
    pagination: PropTypes.shape({
      totalPages: PropTypes.number.isRequired,
      currentPage: PropTypes.number.isRequired,
      currentCount: PropTypes.number.isRequired,
    }),
  }),
}

// -------- PARSING URL ----------

function parseArrayParam(values, valuesMap) {
  return (values || '')
    .split(',')
    .map((valueRaw) => {
      const value = valueRaw.trim()
      if (value in valuesMap) {
        return valuesMap[value]
      }
      return undefined
    })
    .filter((type) => {
      return !!type
    })
}

export function parseTypeParam(typeValues) {
  return parseArrayParam(typeValues, resourceTypeUrlMapping)
}

export function parseNameParam(nameValues) {
  return parseArrayParam(nameValues, nameFilterUrlMapping)
}

// -------- GENERATING URL ----------

function mapFilterToUrlComponents(mapping, values) {
  if (!values) {
    return undefined
  }
  return values.map((value) => {
    return Object.keys(mapping).find((urlComponent) => {
      const internalValue = mapping[urlComponent]
      return internalValue === value
    })
  })
}

function shouldKeepCurrentValue({ newPage, newQuery }) {
  return newPage || newQuery
}

function isStringFilterChanging({ newPage, newQuery }, currentValue, newValue) {
  const current = currentValue || null
  let finalValue = null
  if (newValue) {
    finalValue = newValue
  } else if (shouldKeepCurrentValue({ newPage, newQuery })) {
    finalValue = current
  }
  return current !== finalValue
}

function isArrayFilterChanging({ newPage, newQuery }, currentValues, newValues) {
  const current = currentValues && currentValues.length > 0 ? currentValues.sort() : []
  let finalValues = []
  if (newValues && newValues.length > 0) {
    finalValues = newValues.sort()
  } else if (shouldKeepCurrentValue({ newPage, newQuery })) {
    finalValues = current
  }
  return JSON.stringify(current) !== JSON.stringify(finalValues)
}

function getStringFilterNewValue(currentValue, { newPage, newQuery }, newValue) {
  const isFilterChanging = isStringFilterChanging({ newPage, newQuery }, currentValue, newValue)
  if (newValue) {
    return newValue
  }
  if (!isFilterChanging && currentValue) {
    return currentValue
  }
  return null
}

function getArrayFilterNewValue(currentValues, { newPage, newQuery }, newValues) {
  const isFilterChanging = isArrayFilterChanging({ newPage, newQuery }, currentValues, newValues)
  if (newValues && newValues.length > 0) {
    return newValues.sort()
  }
  if (!isFilterChanging && currentValues.length > 0) {
    return currentValues
  }
  return null
}

function getTypeFilterParams(currentState, stateChanges) {
  const { filters: currentFilters } = currentState
  const { type: currentTypes } = currentFilters
  const { types: newTypes, page: newPage, query: newQuery } = stateChanges

  const filterValue = getArrayFilterNewValue(currentTypes, { newPage, newQuery }, newTypes)
  if (filterValue !== null) {
    const paramValue = mapFilterToUrlComponents(resourceTypeUrlMapping, filterValue)
    return {
      type: paramValue,
    }
  }
  return {}
}

function getNameFilterParams(currentState, stateChanges) {
  const { filters: currentFilters } = currentState
  const { name: currentNames } = currentFilters
  const { names: newNames, page: newPage, query: newQuery } = stateChanges

  const filterValue = getArrayFilterNewValue(currentNames, { newPage, newQuery }, newNames)
  if (filterValue !== null) {
    const paramValue = mapFilterToUrlComponents(nameFilterUrlMapping, filterValue)
    return {
      name: paramValue,
    }
  }
  return {}
}

function getPageParams(currentState, stateChanges) {
  const { filters: currentFilters } = currentState
  const { types: newTypes, names: newNames, page: newPage, query: newQuery } = stateChanges

  const isTypeChanging = isArrayFilterChanging({ newPage, newQuery }, currentFilters.type, newTypes)
  const isNameChanging = isArrayFilterChanging({ newPage, newQuery }, currentFilters.name, newNames)
  const shouldUpdatePage = !isTypeChanging && !isNameChanging

  if (shouldUpdatePage && newPage) {
    return { page: newPage }
  }
  return {}
}

function getQueryFilterParams(currentState, stateChanges) {
  const { filters: currentFilters } = currentState
  const { query: currentQuery } = currentFilters
  const { page: newPage, query: newQuery } = stateChanges

  const filterValue = getStringFilterNewValue(currentQuery, { newPage, newQuery }, newQuery)
  if (filterValue !== null) {
    return {
      query: filterValue,
    }
  }
  return {}
}

function getLibraryUrl(currentState, stateChanges) {
  const typeFilterParams = getTypeFilterParams(currentState, stateChanges)
  const nameFilterParams = getNameFilterParams(currentState, stateChanges)
  const pageParams = getPageParams(currentState, stateChanges)
  const queryFilterParams = getQueryFilterParams(currentState, stateChanges)

  const queryParams = {
    ...typeFilterParams,
    ...nameFilterParams,
    ...pageParams,
    ...queryFilterParams,
  }

  const urlQueryString = queryString.stringify(queryParams, { arrayFormat: 'comma' })
  return `/research-education/library${urlQueryString ? '?' : ''}${urlQueryString}`
}

export function getLibraryUrlGenerator(currentState) {
  return (stateChanges) => getLibraryUrl(currentState, stateChanges)
}

// -------- LIBRARY RESOURCE ICONS ----------

export const libraryResourceIcon = ({ resourceType }) => {
  switch (resourceType) {
    case 'casiSession':
      return libraryResourceIcons.casiSession
    case 'catalogTraining':
      return libraryResourceIcons.catalogTraining
    case 'categoryBrochure':
      return libraryResourceIcons.categoryBrochure
    case 'journalArticle':
      return libraryResourceIcons.journalArticle
    case 'literatureReference':
      return libraryResourceIcons.literatureReference
    case 'patientBrochure':
      return libraryResourceIcons.patientBrochure
    case 'patientEducation':
      return libraryResourceIcons.patientEducation
    case 'pharmacyContent':
      return libraryResourceIcons.pharmacyContent
    case 'priceList':
      return libraryResourceIcons.priceList
    case 'privateLabelForm':
      return libraryResourceIcons.privateLabelForm
    case 'productReferenceChart':
      return libraryResourceIcons.productReferenceChart
    case 'protocol':
      return libraryResourceIcons.protocol
    case 'socialMediaResource':
      return libraryResourceIcons.socialMediaResource
    case 'techSheet':
      return libraryResourceIcons.techSheet
    case 'techSheetFrench':
      return libraryResourceIcons.techSheetFrench
    case 'webinar':
      return libraryResourceIcons.webinar
    case 'whitePaper':
      return libraryResourceIcons.whitePaper
    default:
      return null
  }
}

export const libraryResourceName = ({ resourceType }) => {
  switch (resourceType) {
    case 'casiSession':
      return 'CASI Session Icon'
    case 'catalogTraining':
      return 'Catalog Training Icon'
    case 'categoryBrochure':
      return 'Category Brochure Icon'
    case 'journalArticle':
      return 'Journal Article Icon'
    case 'literatureReference':
      return 'Literature Reference Icon'
    case 'patientBrochure':
      return 'Patient Brochure Icon'
    case 'patientEducation':
      return 'Patient Education Icon'
    case 'pharmacyContent':
      return 'Pharmacy Content Icon'
    case 'priceList':
      return 'Price List Icon'
    case 'privateLabelForm':
      return 'Private Label Form Icon'
    case 'productReferenceChart':
      return 'Product Reference Chart Icon'
    case 'protocol':
      return 'Protocol Icon'
    case 'socialMediaResource':
      return 'Social Media Resource Icon'
    case 'techSheet':
      return 'Tech Sheet Icon'
    case 'techSheetFrench':
      return 'Tech Sheet French Icon'
    case 'webinar':
      return 'Webinar Icon'
    case 'whitePaper':
      return 'White Paper Icon'
    default:
      return null
  }
}
